/**
 * AbnormalPatternMlPage — read-only anomaly report for `abnormal-pattern-ml`.
 *
 * Two panels:
 *   1. Anomaly score distribution — inline SVG bar chart over 5 score buckets.
 *   2. Flagged entities — table of suspicious/abnormal rows sorted by descending score.
 *
 * Styling: single GREEN accent on a light theme. Plain Tailwind only.
 */
import { useMemo } from "react";
import { useAbnormalPatternMlList } from "./hooks";
import {
	bucketScores,
	flaggedOnly,
	sortByScoreDesc,
	summarize,
	type AbnormalPatternMlRow,
} from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH = "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900 tabular-nums";
const STAT_LABEL = "text-xs font-medium uppercase tracking-wider text-zinc-500";
const STAT_VALUE = "mt-1 text-2xl font-semibold text-zinc-900 tabular-nums";

function classificationChip(c: AbnormalPatternMlRow["classification"]) {
	// Green-only theme: intensity differs by weight, never hue.
	if (c === "abnormal") return "bg-green-100 text-green-900 ring-1 ring-green-700";
	if (c === "suspicious") return "bg-green-50 text-green-800 ring-1 ring-green-500";
	return "bg-zinc-50 text-zinc-700 ring-1 ring-zinc-300";
}

function formatEpoch(sec: number): string {
	if (!Number.isFinite(sec) || sec <= 0) return "-";
	const d = new Date(sec * 1000);
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	const hh = String(d.getUTCHours()).padStart(2, "0");
	const mm = String(d.getUTCMinutes()).padStart(2, "0");
	return `${y}-${m}-${day} ${hh}:${mm} UTC`;
}

export function AbnormalPatternMlPage() {
	const listQuery = useAbnormalPatternMlList();
	const items = listQuery.data?.items ?? [];

	const summary = useMemo(() => summarize(items), [items]);
	const buckets = useMemo(() => bucketScores(items), [items]);
	const flagged = useMemo(() => sortByScoreDesc(flaggedOnly(items)), [items]);
	const maxBucketCount = Math.max(1, ...buckets.map((b) => b.count));

	return (
		<main
			data-testid="abnormal-pattern-ml-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Report
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Abnormal pattern ML
				</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Per-event anomaly scores from the feature-weighted model. Higher scores flag
					more suspicious traffic.
				</p>
			</header>

			<section
				aria-label="Summary"
				className="grid grid-cols-2 gap-3 sm:grid-cols-4"
				data-testid="abnormal-pattern-ml-summary"
			>
				<div className={CARD}>
					<div className={STAT_LABEL}>Total events</div>
					<div className={STAT_VALUE} data-testid="summary-total">
						{summary.total.toLocaleString()}
					</div>
				</div>
				<div className={CARD}>
					<div className={STAT_LABEL}>Avg score</div>
					<div className={STAT_VALUE} data-testid="summary-avg-score">
						{summary.avgScore.toFixed(1)}
					</div>
				</div>
				<div className={CARD}>
					<div className={STAT_LABEL}>Suspicious</div>
					<div className={STAT_VALUE} data-testid="summary-suspicious">
						{summary.suspicious.toLocaleString()}
					</div>
				</div>
				<div className={CARD}>
					<div className={STAT_LABEL}>Abnormal</div>
					<div className={STAT_VALUE} data-testid="summary-abnormal">
						{summary.abnormal.toLocaleString()}
					</div>
				</div>
			</section>

			<section className={CARD} aria-label="Anomaly score distribution">
				<div className="flex items-baseline justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Anomaly score distribution
					</h2>
					<span className="text-xs text-zinc-500">{summary.total.toLocaleString()} events</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="abnormal-pattern-ml-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-4 text-sm text-red-700" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="abnormal-pattern-ml-chart-empty"
					>
						No data.
					</p>
				) : (
					<svg
						role="img"
						aria-label="Anomaly score distribution bar chart"
						viewBox="0 0 500 200"
						className="mt-4 h-56 w-full"
						data-testid="abnormal-pattern-ml-chart"
					>
						<title>Anomaly score distribution</title>
						{buckets.map((b, i) => {
							const barWidth = 72;
							const gap = 20;
							const leftPad = 32;
							const x = leftPad + i * (barWidth + gap);
							const chartH = 150;
							const topPad = 10;
							const h = Math.round((b.count / maxBucketCount) * chartH);
							const y = topPad + (chartH - h);
							return (
								<g key={b.label} data-testid={`abnormal-pattern-ml-bar-${b.label}`}>
									<rect
										x={x}
										y={y}
										width={barWidth}
										height={h}
										rx={4}
										ry={4}
										className="fill-green-600"
									/>
									<text
										x={x + barWidth / 2}
										y={y - 4}
										textAnchor="middle"
										className="fill-zinc-900 text-[11px] font-semibold"
									>
										{b.count}
									</text>
									<text
										x={x + barWidth / 2}
										y={chartH + topPad + 18}
										textAnchor="middle"
										className="fill-zinc-600 text-[11px]"
									>
										{b.label}
									</text>
								</g>
							);
						})}
						<line
							x1={20}
							y1={160}
							x2={480}
							y2={160}
							className="stroke-zinc-300"
							strokeWidth={1}
						/>
					</svg>
				)}
			</section>

			<section className={CARD} aria-label="Flagged entities">
				<div className="flex items-baseline justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Flagged entities</h2>
					<span className="text-xs text-zinc-500">
						{flagged.length.toLocaleString()} suspicious or abnormal
					</span>
				</div>

				{listQuery.isLoading ? (
					<p className="mt-4 text-sm text-zinc-500">Loading.</p>
				) : items.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="abnormal-pattern-ml-empty"
					>
						No data.
					</p>
				) : flagged.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="abnormal-pattern-ml-no-flagged"
					>
						No flagged events.
					</p>
				) : (
					<div className="mt-4 overflow-x-auto">
						<table
							className="w-full border-collapse"
							data-testid="abnormal-pattern-ml-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>Event</th>
									<th className={TH}>Source</th>
									<th className={TH}>Classification</th>
									<th className={TH}>Score</th>
									<th className={TH}>Reason</th>
									<th className={TH}>Seen</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{flagged.map((row) => (
									<tr
										key={row.id}
										data-testid={`abnormal-pattern-ml-row-${row.id}`}
									>
										<td className={TD}>{row.event_id}</td>
										<td className={TD}>{row.source}</td>
										<td className={TD}>
											<span
												className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${classificationChip(row.classification)}`}
											>
												{row.classification}
											</span>
										</td>
										<td className={TD}>{row.score.toLocaleString()}</td>
										<td className={TD}>{row.reason || "-"}</td>
										<td className={TD}>{formatEpoch(row.created_at)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default AbnormalPatternMlPage;
